(() => {
  angular.module('app').controller('common.views.inpatientNprClaims.process', ControllerController);

  ControllerController.$inject = [
    '$scope',
    '$state',
    '$stateParams',
    '$timeout',
    'abp.services.app.inpatientPatientTicket',
    'moment',
  ];

  function ControllerController(
    $scope, $state, $stateParams, $timeout, inpatientPatientTicketSvc, moment) {
    const vm = this;

    vm.loading = 0;
    vm.isHost = App.isHost();
    vm.claimNumber = $stateParams.claimNumber;
    vm.currencyCode = abp.setting.get('Hms.General.CurrencyCode');
    vm.ticket = null;
    vm.save = save;
    vm.hospitalizationBenefits = [];

    vm.hasSobEnhancement = abp.setting.getBoolean('Hms.Feature.SobEnhancement');
    vm.hasInsuranceEnhancementModule = abp.setting.getBoolean('Hms.Feature.InsuranceEnhancementModule');

    vm.coPay = {
      isRoomAndBoardExceedEntitlement: false,
      isCoInsurance: false,
      isPercentage: false,
      coPayExclusions: [],
      value: 0,
    };

    vm.roomAndBoardCharges = 0;

    // HM-11151 — ACE auto-fill. Keyed by ticket field name; each entry holds what the member
    // originally submitted so the field's ACE indicator can show it (see CONTEXT.md, ACE
    // auto-fill). Stays null until ACE actually fills something, so no indicator renders on a
    // claim with no analysis.
    vm.aceFilledFields = null;
    vm.onAceAnalysisReady = onAceAnalysisReady;

    // The box fetches its own summary, so ACE's result and the claim form arrive independently
    // and in either order. Whichever lands second triggers the fill.
    let pendingAceAnalysis = null;

    // What the ACE-targeted fields held when the form finished loading — the baseline that tells
    // a pristine form apart from one the processor has started editing.
    let loadedAceTargets = null;

    init();

    // vm.hospitalizationBenefits is written back by the benefit-coverage component after its own,
    // later round trip. An ACE analysis that arrived before then is still pending (see
    // applyPendingAceAnalysis) and gets applied here once the benefits land.
    $scope.$watchCollection(
      () => vm.hospitalizationBenefits,
      () => applyPendingAceAnalysis()
    );

    function init() {
      vm.loading += 1;

      inpatientPatientTicketSvc
        .processClaim({
          id: vm.claimNumber,
        })
        .success((data) => {
          vm.isLoading = true;

          vm.patient = data.patient || {};
          vm.organization = data.organization || {};
          vm.hasSimilarClaimSubmitted = data.hasSimilarClaimSubmitted;
          vm.bankDetails = data.bankDetails || {};
          vm.categories = data.categories || [];
          vm.diseaseClassifications = data.diseaseClassifications || [];
          vm.diseaseClassifications = _.map(vm.diseaseClassifications, (d) => ({
            id: d.id,
            code: d.code,
            description: `(${d.code}) ${d.description}`,
            parentDescription: `(${d.parentCode}) ${d.parentDescription}`,
          }));

          // Initialize ticket dto.

          vm.ticket = {
            inpatientClaimNumber: data.inpatientClaimNumber,
            receiptDate: data.receiptDate,
            receiptAmount: data.receiptAmount,
            serviceType: data.serviceType,
            claimType: data.claimType,
            manualClinicName: data.manualClinicName,
            manualClinicLocation: data.manualClinicLocation,
            manualDoctorName: data.doctors,
            admissionDate: data.admissionDate,
            dischargeDate: data.dischargeDate,
            newAttachments: [],
            attachments: data.attachments,
            mcAttachments: data.mcAttachments,
            surgicalAttachments: data.surgicalAttachments,
            consentFormAttachments: data.consentFormAttachments,
            identificationAttachments: data.identificationFormAttachments,
            originalReceiptAttachments: data.originalReceiptAttachments,
            billDetailsAttachments: data.billDetailsAttachments,
            medicalReportAttachments: data.medicalReportAttachments,
            investigationReportAttachments: data.investigationReportAttachments,
            physiotherapyReceiptAttachments: data.physiotherapyReceiptAttachments,
            policeReportAttachments: data.policeReportAttachments,
            allowance: data.patient.allowance,
            diagnoses: data.finalDiagnoses,
            isNewVersion: data.isNewVersion,
            rooms: [],
            procedures: [],
            others: [],
            doctorProcedures: [],
            hospitalCharges: [],
            accommodationChargesTreatmentCategories: data.accommodationChargesTreatmentCategories,
            inHospitalDoctorVisitTreatmentCategories: data.inHospitalDoctorVisitTreatmentCategories,
            doctorProcedureChargesTreatmentCategories: data.doctorProcedureChargesTreatmentCategories,
            hospitalChargesTreatmentCategories: data.hospitalChargesTreatmentCategories,
            emergencyOutpatientTreatmentCategories: data.emergencyOutpatientTreatmentCategories,
            miscellaneousTreatmentCategories: data.miscellaneousTreatmentCategories,
            currencyCode: data.currencyCode,
          };

          vm.isMaternity = data.serviceType === 8;

          vm.coPayTypeName = data.coPayTypeName;

          loadedAceTargets = captureAceTargets();

          applyPendingAceAnalysis();
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    /* ACE auto-fill (HM-11151) */

    // Called by the ACE Results Box once ACE has a readable result. Never called for the
    // not-submitted, pending, failed or timed-out states, so those claims keep the form exactly
    // as the member submitted it.
    function onAceAnalysisReady(analysis) {
      pendingAceAnalysis = analysis;
      applyPendingAceAnalysis();
    }

    // ACE's extraction overwrites the member's own entries, because ACE read the discharge
    // summary and the member did not. The indicator tooltips carry ACE's reasoning for each value
    // (product review item 6) — not the previous entry — so a fill over the processor's own work
    // is gated behind their explicit consent below rather than assumed recoverable.
    function applyPendingAceAnalysis() {
      if (!pendingAceAnalysis || !vm.ticket) return;

      // Only the standard NPR ticket component renders the ACE indicators, and on a maternity
      // claim it never will — drop the analysis outright.
      if (vm.isMaternity) {
        pendingAceAnalysis = null;
        return;
      }

      // canAutoFill()'s other input, vm.hospitalizationBenefits, loads on its own later round
      // trip, so a false here may only mean "not loaded yet". Keep the analysis pending — the
      // watcher above re-runs this when the benefits land. If they never make the indicators
      // render, the analysis simply stays unapplied, exactly like the indicators themselves.
      if (!canAutoFill()) return;

      const analysis = pendingAceAnalysis;
      pendingAceAnalysis = null;

      // A fill that lands after the processor has started editing replaces their work wholesale.
      // That needs their say-so — for the snapshotted target fields, and equally for curated
      // hospitalisation leave dates: writing either stay date makes the hospitalizationDays
      // component clear and rebuild that array, even when ACE's date equals the current value.
      if (
        aceTargetsTouchedSinceLoad() ||
        (aceFillWritesStayDates(analysis) && leaveDatesCurated())
      ) {
        abp.message.confirm(
          App.localize('AceApplyLateResultsPrompt'),
          App.localize('AceResults'),
          (confirmed) => {
            if (confirmed) {
              $timeout(() => applyAceAnalysis(analysis));
            }
          }
        );
        return;
      }

      applyAceAnalysis(analysis);
    }

    // Mirrors the npr-ticket-info component's own ng-if — the one place the indicators render.
    function canAutoFill() {
      return (
        !!vm.ticket &&
        !vm.isMaternity &&
        ((vm.hospitalizationBenefits || []).length > 0 || !vm.ticket.isNewVersion)
      );
    }

    // The ACE-targeted fields exactly as they stood when the form finished loading.
    function captureAceTargets() {
      return {
        admissionDate: dateStamp(vm.ticket.admissionDate),
        dischargeDate: dateStamp(vm.ticket.dischargeDate),
        manualDoctorName: vm.ticket.manualDoctorName || '',
        manualClinicName: vm.ticket.manualClinicName || '',
        diagnoses: _.map(vm.ticket.diagnoses || [], diagnosisIdentity).join(','),
      };
    }

    // Free-text diagnoses arrive from members as a description with no code, and the
    // finalDiagnosis component then mints a negative placeholder code for them — after this
    // page's load snapshot. Key those rows by description instead, so the minting itself never
    // reads as a processor edit; a real ICD-10 selection keeps the code as the identity.
    function diagnosisIdentity(diagnosis) {
      return _.isNil(diagnosis.code) || diagnosis.code < 0
        ? `text:${diagnosis.description || ''}`
        : String(diagnosis.code);
    }

    function aceTargetsTouchedSinceLoad() {
      // No snapshot means the fill is racing the form load itself; nothing is touched yet.
      if (!loadedAceTargets) return false;

      return !angular.equals(captureAceTargets(), loadedAceTargets);
    }

    function dateStamp(value) {
      return value ? moment(value).valueOf() : null;
    }

    // The leave dates cannot join the load snapshot: the hospitalizationDays component only
    // creates vm.ticket.hospitalizationLeaveDates after this page's success handler has run, so a
    // snapshot taken there would read every later fill as touched. Curation is derived instead:
    // this page always opens on the untouched full stay expansion (the component's own watchers
    // normalise it on the first digest), so any difference from it is the processor's doing.
    function leaveDatesCurated() {
      const leaveDates = vm.ticket.hospitalizationLeaveDates;
      if (!_.isArray(leaveDates)) return false;

      const fullRange = fullStayRange();
      if (!fullRange) return leaveDates.length > 0;

      return !angular.equals(_.uniq(leaveDates).sort(), fullRange);
    }

    // The inclusive day-per-entry expansion the hospitalizationDays component rebuilds whenever a
    // stay date is written — its updateHospitalizationLeaveDates, replicated. Null when either
    // stay date is missing or unparseable. Replicates the WATCHER path only: the component's
    // init() variant (startOf('day').utc()) shifts a day on positive UTC offsets, but its HM-4165
    // guards compare a moment against the raw binding with !== (always true), so the watchers
    // refire on the first digest and overwrite init()'s array before anything observes it. If
    // those guards are ever made real comparisons, derive this from the child's own array after
    // its init instead.
    function fullStayRange() {
      if (!vm.ticket.admissionDate || !vm.ticket.dischargeDate) return null;

      const start = moment(vm.ticket.admissionDate);
      const end = moment(vm.ticket.dischargeDate);
      if (!start.isValid() || !end.isValid()) return null;

      const range = [];
      const endStamp = end.format('YYYY-MM-DD');
      let cursor = start.format('YYYY-MM-DD');
      while (cursor <= endStamp) {
        range.push(cursor);
        cursor = moment(cursor).add(1, 'days').format('YYYY-MM-DD');
      }
      return range;
    }

    // Whether the fill would write a stay date at all — not whether it would change one. fillDate
    // always assigns a fresh Date object and the component's watchers compare by reference, so
    // writing an unchanged value still clears and rebuilds the leave dates.
    function aceFillWritesStayDates(analysis) {
      return (
        fillableDate(clinicalNode(analysis, 'admissionDate')) ||
        fillableDate(clinicalNode(analysis, 'dischargeDate'))
      );
    }

    function fillableDate(node) {
      return !!node && !!node.value && moment(node.value).isValid();
    }

    function applyAceAnalysis(analysis) {
      const filled = {};

      fillDate(filled, 'admissionDate', clinicalNode(analysis, 'admissionDate'));
      fillDate(filled, 'dischargeDate', clinicalNode(analysis, 'dischargeDate'));
      fillDoctor(filled, analysis);
      fillIssuer(filled, analysis);
      fillDiagnoses(filled, analysis);
      markHospitalizationDays(filled, analysis);

      vm.aceFilledFields = _.isEmpty(filled) ? null : filled;
    }

    // Hospitalisation days is never written here. The hospitalizationDays component recalculates it
    // from the admission and discharge dates and watches both, so filling the dates fills the
    // count; writing it directly would fight that derivation and could leave the count disagreeing
    // with hospitalizationLeaveDates, which is the array that actually persists.
    //
    // It is still ACE-derived once ACE has supplied the dates, so it carries the indicator. ACE's
    // lengthOfStay goes into the tooltip rather than the field because it is a different measure —
    // nights, where this field counts inclusive leave days, so a 25th-to-30th stay is 5 to ACE and
    // 6 here. Neither number corrects the other.
    function markHospitalizationDays(filled, analysis) {
      if (!filled.admissionDate && !filled.dischargeDate) return;

      // The rebuild needs both stay dates: with either missing, the hospitalizationDays component
      // early-returns and the count stays put, so a chip claiming recalculation would lie.
      if (!vm.ticket.admissionDate || !vm.ticket.dischargeDate) return;

      const node = clinicalNode(analysis, 'lengthOfStay');
      const reported = _.trim((node || {}).value || '');

      filled.hospitalizationDays = {
        tooltip: reported
          ? App.localize('AceReportedXNights', reported)
          : App.localize('AceRecalculatedFromAceDates'),
      };
    }

    // Every clinicalDetailsInformation node is nullable and wraps its value alongside a
    // confidence score.
    function clinicalNode(analysis, key) {
      const clinicalDetails = (analysis.medicalAnalysis || {}).clinicalDetailsInformation || {};
      const node = clinicalDetails[key];

      return _.isObject(node) ? node : null;
    }

    // §11 names confidence `confidenceScore` on outcome checks but `confidence` on the
    // medicalAnalysis nodes read here (the shipped Phase 1.5 full-analysis view is the reference —
    // no schema is checked in). Read both so a payload matching either shape keeps its tooltip.
    function nodeConfidence(node) {
      return _.isNil(node.confidence) ? node.confidenceScore : node.confidence;
    }

    // The indicator's tooltip carries ACE's own reasoning where the §11 payload has one, and the
    // extraction confidence where it does not (product review: reasoning, not the previous
    // value). Composed here, at fill time — the components just display it.
    function aceTooltip(reason, confidenceScore) {
      const confidence =
        confidenceScore === null || confidenceScore === undefined
          ? null
          : `${Math.round(confidenceScore * 100)}%`;
      const trimmedReason = _.trim(reason || '');

      if (trimmedReason) {
        return confidence ? `${trimmedReason} (${confidence})` : trimmedReason;
      }

      return confidence
        ? App.localize('AceExtractedWithConfidence', confidence)
        : App.localize('FilledFromAceAnalysis');
    }

    function fillDoctor(filled, analysis) {
      const doctors = asObjects((analysis.medicalAnalysis || {}).doctors);
      const doctor = _.find(doctors, 'isPrimary') || _.first(doctors);
      if (!doctor) return;

      fillText(filled, 'manualDoctorName', doctor.name, aceTooltip(doctor.reason, nodeConfidence(doctor)));
    }

    // The panel name comes from the bill's issuer. Bills from more than one issuer cannot be
    // reduced to a single panel name, so nothing is filled rather than picking one arbitrarily.
    // Trimmed before comparing, and non-strings discarded: ACE pads its issuer names, so "Panel X"
    // and "Panel X " are one issuer rather than two, and a malformed value cannot be coerced into
    // the field.
    function fillIssuer(filled, analysis) {
      const issuers = _.uniq(
        _.filter(
          _.map(asObjects((analysis.billingAnalysis || {}).bills), (bill) =>
            _.isString(bill.issuer) ? _.trim(bill.issuer) : ''
          )
        )
      );
      if (issuers.length !== 1) return;

      fillText(filled, 'manualClinicName', issuers[0], aceTooltip(null, null));
    }

    // Primary diagnosis first, and only codes the portal actually knows — ACE cannot introduce an
    // ICD-10 code that is not in the disease classification list.
    function fillDiagnoses(filled, analysis) {
      const diagnoses = asObjects((analysis.medicalAnalysis || {}).diagnoses);
      const matched = _.uniqBy(
        _.filter(
          _.map(_.orderBy(diagnoses, ['isPrimary'], ['desc']), (diagnosis) =>
            _.find(vm.diseaseClassifications, { code: diagnosis.icd10Code })
          )
        ),
        'code'
      );

      if (!matched.length) return;

      // The section chip's tooltip carries the primary diagnosis's reasoning — the §11 payload
      // reasons per diagnosis, and the primary one is why the claim is what it is.
      const primary = _.find(diagnoses, 'isPrimary') || _.first(diagnoses);

      filled.diagnoses = { tooltip: aceTooltip(primary.reason, nodeConfidence(primary)) };
      vm.ticket.diagnoses = _.map(matched, (classification) => ({
        code: classification.code,
        description: classification.description,
        parentDescription: classification.parentDescription,
        // ACE's §11 diagnosis node carries no date, but the field is required and is bounded by
        // the admission and discharge dates. Default to the discharge date — which is what the
        // field's own validation calls it — so an auto-filled form opens valid. Cloned per row so
        // editing one row's date cannot move the others.
        diagnosisDate: vm.ticket.dischargeDate ? new Date(vm.ticket.dischargeDate) : null,
      }));
    }

    function fillDate(filled, field, node) {
      if (!fillableDate(node)) return;

      const target = filled;
      target[field] = { tooltip: aceTooltip(node.reason, nodeConfidence(node)) };
      vm.ticket[field] = moment(node.value).toDate();
    }

    function fillText(filled, field, value, tooltip) {
      const trimmed = _.trim(value || '');
      if (!trimmed) return;

      const target = filled;
      target[field] = { tooltip };
      vm.ticket[field] = trimmed;
    }

    // Nullable ACE arrays can carry null/garbage elements; only real objects are read.
    function asObjects(list) {
      return _.filter(_.isArray(list) ? list : [], _.isObject);
    }

    function save() {
      vm.loading += 1;

      var rooms = [];
      var procedures = [];
      var others = [];
      var doctorProcedures = [];
      var hospitalCharges = [];
      var emergencyOutpatientTreatments = [];
      var miscellaneousTreatments = [];
      if (vm.hasInsuranceEnhancementModule && !vm.isMaternity){
        rooms = formatTicketItems(
          vm.ticket.rooms,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.category,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
            startDate: x.roomChargeDateRangeModel.startDate
              ? moment(x.roomChargeDateRangeModel.startDate).utc()
              : null,
            endDate: x.roomChargeDateRangeModel.endDate
              ? moment(x.roomChargeDateRangeModel.endDate).utc()
              : null,
            dailyCharges: x.dailyCharges,
            lengthOfStay: x.lengthOfStay
          }));
        procedures = formatTicketItems(
          vm.ticket.procedures,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.doctorType,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
            startDate: x.visitDate ? moment(x.visitDate).utc() : null,
            endDate: x.visitDate ? moment(x.visitDate).utc() : null,
            doctorId: x.doctorId,
            totalVisit: 1,
            visit: x.visit
          }));
        doctorProcedures = formatTicketItems(
          vm.ticket.doctorProcedures,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.category,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
            startDate: x.visitDate ? moment(x.visitDate).utc() : null,
            endDate: x.visitDate ? moment(x.visitDate).utc() : null,
            doctorId: x.doctorId
          }));
        hospitalCharges = formatTicketItems(
          vm.ticket.hospitalCharges,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.category,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
          }));
        emergencyOutpatientTreatments = formatTicketItems(
          vm.ticket.emergencyOutpatientTreatments,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.mainTreatment,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
          }));

        miscellaneousTreatments = formatTicketItems(
          vm.ticket.miscellaneousTreatments,
          (x) => ({
            amount: x.amount,
            uncoveredAmount: x.uncoveredAmount,
            category: x.section,
            categoryName: x.mainTreatment,
            subCategory: x.subTreatment,
            procedure: x.procedure,
            uncoveredProcedure: x.uncoveredProcedure,
            remark: x.remark,
            uncoveredRemark: x.uncoveredRemark,
          }));
      }
      else if (vm.hasSobEnhancement && !vm.isMaternity) {
        _.each(vm.ticket.rooms, (x) => {
          _.each(x.ticketItems, (item) => {
            const p = item;
            if (!Number.isNaN(Number(p.totalCharges))) {
              p.startDate = moment(p.roomChargeDateRangeModel.startDate).utc();
              p.endDate = moment(p.roomChargeDateRangeModel.endDate).utc();
              rooms.push(p);
            }
          });
        });

        _.each(vm.ticket.procedures, (x) => {
          _.each(x.ticketItems, (item) => {
            const p = item;
            if (!Number.isNaN(Number(p.totalCharges))) {
              p.startDate = p.visitDate ? moment(p.visitDate).utc() : null;
              p.endDate = p.visitDate ? moment(p.visitDate).utc() : null;

              // Total visit is always 1.

              if (!p.totalVisit || p.totalVisit !== 1) p.totalVisit = 1;

              procedures.push(p);
            }
          });
        });

        _.each(vm.ticket.doctorProcedures, (x) => {
          _.each(x.ticketItems, (p) => {
            if (!Number.isNaN(Number(p.totalCharges))) {
              doctorProcedures.push(p);
            }
          });
        });

        _.each(vm.ticket.hospitalCharges, (x) => {
          _.each(x.ticketItems, (p) => {
            if (!Number.isNaN(Number(p.totalCharges))) {
              hospitalCharges.push(p);
            }
          });
        });
      }
      _.each(vm.ticket.others, (x) => {
        if (typeof x.category !== 'undefined' && x.category !== null) others.push(x);
      });

      const input = $.extend(
        {},
        {
          id: vm.claimNumber,
          inpatientClaimNumber: vm.ticket.inpatientClaimNumber,
          receiptDate: vm.ticket.receiptDate,
          receiptAmount: vm.ticket.receiptAmount,
          serviceType: vm.ticket.serviceType,
          claimType: vm.ticket.claimType,
          attachments: vm.ticket.newAttachments,
          diagnoses: vm.ticket.diagnoses,
          rooms,
          procedures,
          others,
          doctorProcedures,
          hospitalCharges,
          emergencyOutpatientTreatments,
          miscellaneousTreatments,
          panelName: vm.ticket.manualClinicName,
          panelAddress: vm.ticket.manualClinicLocation,
          admissionDate: vm.ticket.admissionDate,
          dischargeDate: vm.ticket.dischargeDate,
          medicalCertificate: vm.ticket.medicalCertificate,
          doctor: vm.ticket.manualDoctorName,
          patient: vm.patient,
          paymentEmailAddresses: vm.bankDetails.emailAddresses,
          newAttachments: vm.ticket.newAttachments,
          hospitalizationDays: vm.ticket.hospitalizationDays,
          hospitalizationLeaveDates: vm.ticket.hospitalizationLeaveDates,
        }
      );

      inpatientPatientTicketSvc
        .createInpatientNPRTicket(input)
        .success((data) => {
          abp.notify.info(App.localize('TicketXIssued', data));
          $state.go('inpatientTicketDetail', {
            ticketNumber: data,
          });
        })
        .finally(() => {
          vm.loading -= 1;
        });
    }

    function formatTicketItems(treatments, format) {
      const items = []
      _.each(treatments, (x) => {
        _.each(x.ticketItems, (y) => {
          if (!y.mainTreatment && !y.category && !y.doctorType) return;
          const ticketItem = angular.copy(y);
          items.push(format(ticketItem));
        });
      });

      return items;
    }
  }
})();
